import { useEffect, useState } from "react";
import { useLang } from "@/i18n/LanguageContext";
import { ChevronRight, Menu, X } from "lucide-react";

const Navbar = () => {
  const { lang, setLanguage, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "services", label: t("nav.services") },
    { id: "about", label: t("nav.about") },
    { id: "process", label: t("nav.process") },
    { id: "gallery", label: t("nav.gallery") },
    { id: "trust", label: t("nav.trust") },
    { id: "faq", label: t("nav.faq") },
    { id: "contact", label: t("nav.contact") },
  ];

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#0a0a0a]/85 backdrop-blur-xl border-b border-[#1f1f1f]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <a
          href="#top"
          data-testid="logo-link"
          className="flex items-center gap-3 group"
        >
          <span className="w-9 h-9 bg-[#dc2626] text-white font-display font-black text-base flex items-center justify-center group-hover:rotate-3 transition-transform">
            MF
          </span>
          <div className="hidden sm:flex flex-col leading-none">
            <span className="font-display font-black text-sm tracking-tight uppercase">
              Dobra Lakiernia
            </span>
            <span className="font-mono text-[10px] tracking-[0.2em] text-[#71717a] uppercase mt-0.5">
              Proszkowa · Nowy Dwór Maz.
            </span>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <a
              key={l.id}
              href={`#${l.id}`}
              data-testid={`nav-${l.id}`}
              className="text-[13px] tracking-wide text-[#a1a1aa] hover:text-white transition-colors font-medium"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div
            data-testid="lang-switcher"
            className="hidden md:flex items-center border border-[#27272a] font-mono text-[11px]"
          >
            <button
              type="button"
              data-testid="lang-pl"
              onClick={() => setLanguage("pl")}
              className={`px-2.5 py-1.5 transition-colors ${
                lang === "pl"
                  ? "bg-[#dc2626] text-white"
                  : "text-[#a1a1aa] hover:text-white"
              }`}
            >
              PL
            </button>
            <button
              type="button"
              data-testid="lang-en"
              onClick={() => setLanguage("en")}
              className={`px-2.5 py-1.5 transition-colors ${
                lang === "en"
                  ? "bg-[#dc2626] text-white"
                  : "text-[#a1a1aa] hover:text-white"
              }`}
            >
              EN
            </button>
          </div>

          <a
            href="#contact"
            data-testid="nav-cta"
            className="hidden md:inline-flex items-center gap-2 bg-[#dc2626] text-white font-semibold text-xs tracking-wider uppercase px-4 py-2.5 hover:bg-[#b91c1c] transition-colors"
          >
            {t("nav.cta")}
            <ChevronRight className="w-3.5 h-3.5" />
          </a>

          <button
            type="button"
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 text-white"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-[#0a0a0a] border-t border-[#1f1f1f]"
        >
          <div className="px-6 py-4 flex flex-col gap-1">
            {links.map((l) => (
              <a
                key={l.id}
                href={`#${l.id}`}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.id}`}
                className="py-2.5 text-sm text-[#a1a1aa] hover:text-white border-b border-[#1f1f1f]"
              >
                {l.label}
              </a>
            ))}
            <div className="flex items-center gap-3 pt-4">
              <button
                type="button"
                onClick={() => setLanguage("pl")}
                className={`px-3 py-1.5 text-xs font-mono border border-[#27272a] ${
                  lang === "pl" ? "bg-[#dc2626] text-white" : "text-white"
                }`}
              >
                PL
              </button>
              <button
                type="button"
                onClick={() => setLanguage("en")}
                className={`px-3 py-1.5 text-xs font-mono border border-[#27272a] ${
                  lang === "en" ? "bg-[#dc2626] text-white" : "text-white"
                }`}
              >
                EN
              </button>
              <a
                href="#contact"
                onClick={() => setOpen(false)}
                className="ml-auto bg-[#dc2626] text-white px-4 py-2 text-xs font-semibold uppercase"
              >
                {t("nav.cta")}
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
